import { Button, Skeleton } from '@/shared/ui';
import { Text } from '../../text';

type PaginationProps = {
  /** サーバーから取得した全体の件数 */
  totalCount: number;
  /** ローディング状態 */
  isLoading?: boolean;
  pagination: {
    /** ページ番号（1ベース、URLパラメータと同じ） */
    page: number | undefined;
    pageSize: number | undefined;
  };
  /**
   * ページ移動コールバック（1ベース）。
   * TanStack Table の setPageIndex を経由しないため、pageCount による暗黙の clamp を受けない。
   */
  onPageChange: (page: number) => void;
};

export const Pagination = ({
  totalCount,
  isLoading = false,
  pagination,
  onPageChange,
}: PaginationProps) => {
  const pageSize = pagination.pageSize ?? 10;
  const totalItems = totalCount;
  const totalPages = Math.ceil(totalCount / pageSize);

  if (isLoading) {
    return (
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-48" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
    );
  }

  if (totalItems <= 0) {
    return null;
  }

  const lastPage = totalPages <= 0 ? 1 : totalPages;
  // URL 直編集等で範囲外の page が来た場合の防御。件数表示や Prev/Next の整合を保つ。
  const currentPage = Math.min(Math.max(pagination.page ?? 1, 1), lastPage);

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // 全ページ列挙→filter ではなく候補ページを直接構築（1000 ページ規模での無駄な配列生成を避ける）
  const visiblePages = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, lastPage])]
    .filter((page) => page >= 1 && page <= lastPage)
    .sort((a, b) => a - b);

  return (
    <div className="flex items-center justify-between">
      <Text color="muted">
        全 {totalItems} 件中 {startItem} - {endItem} 件を表示
      </Text>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
        >
          前へ
        </Button>
        {visiblePages.map((page, index, array) => (
          <div key={page} className="contents">
            {index > 0 && array[index - 1] !== page - 1 && (
              <Text as="span" className="px-2">
                ...
              </Text>
            )}
            <Button
              type="button"
              variant={page === currentPage ? 'outline' : 'ghost'}
              size="sm"
              onClick={() => onPageChange(page)}
            >
              {page}
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= lastPage}
        >
          次へ
        </Button>
      </div>
    </div>
  );
};
